import React, { useState, useRef, Dispatch, SetStateAction } from 'react';
import classnames from 'classnames';
import { i18n } from '@/utils';
import { Drawer, Loading } from '@/components';
import Icon from './share-icon';
import { ShareType } from './share';
import styles from './share.less';

interface ShareLinksProps {
  /** 分享链接 */
  shareLink?: string;
  /** 分享图片链接 */
  shareImageURL: { url: string; base64: string };
  /** 分享文案 */
  shareText: string;
  /** 分享事件分类 */
  shareEventCategory?: string;
  /** gtag 统计 */
  gtag: any;
  /** 设置分享状态 */
  setShareStatus: Dispatch<SetStateAction<ShareType>>;
  /** 设置分享加载 */
  setShareLoading: Dispatch<SetStateAction<boolean>>;
  /** 关闭回调 */
  onClose(): void;
}

const ShareLinks: React.FC<ShareLinksProps> = ({ shareLink, shareImageURL, shareText, shareEventCategory, gtag, setShareStatus, setShareLoading, onClose }) => {

  /** 进度条 DOM */
  const _progress = useRef<HTMLDivElement>(null);
  /** 完成条 DOM */
  const _percent = useRef<HTMLDivElement>(null);
  /** 分享图片加载是否完成 */
  const [ isLoadedShareImg, setIsLoadedShareImg ] = useState<boolean>(false);

  const shareURL = (source: string) => encodeURIComponent(`${shareLink}/${shareImageURL.url.split('.png')[0]}__png_tt_${source}`);

  /** 分享回调 */
  const onShare = (source: string) => {
    setShareLoading(true);
    gtag('event', `sunzi_share_${source}`, {
      'event_category': shareEventCategory || 'newsock_share'
    });
    setTimeout(() => {
      onClose();
      setShareStatus(ShareType.Shared);
      setShareLoading(false);
    }, 5000);
  }

  /** 取消分享 */
  const handleClose = () => {
    onClose();
    gtag('event', `sunzi_share_cancel`, {
      'event_category': shareEventCategory || 'newsock_share'
    });
  }

  return (
    <div className={styles.shareLinks}>
      <Drawer
        className={styles.shareLinksContent}
        onClose={handleClose}
        mask
      >
        <div className={styles.shareLinksInfo}>
          <div className={styles.shareImage}>
            {!isLoadedShareImg &&
              <div className={styles.shareImageLoading}>
                <Loading size={50} color="#c0c4cc" />
                <div className={styles.loadingText}>{i18n.format('modules.new.sock.generating.image')}</div>
                <div ref={_progress} className={styles.loadingProgress}>
                  <div
                    ref={_percent}
                    className={classnames(styles.progressPercent, styles.progressPercentImitate)}
                  />
                </div>
              </div>
            }
            {shareImageURL.base64 &&
              <img
                src={shareImageURL.base64}
                className={classnames({
                  [styles.imageLoading]: !isLoadedShareImg,
                })}
                onLoad={() => setIsLoadedShareImg(true)}
              />
            }
          </div>
          <div className={styles.shareText}>
            <div className={styles.url}>{window.location.host}</div>
            <div className={styles.detail}>{shareText}</div>
          </div>
        </div>
        <div className={styles.buttonLayout}>
          <a
            className={classnames(styles.facebook, {
              [styles.disable]: !isLoadedShareImg,
            })}
            href={`https://www.facebook.com/sharer/sharer.php?u=${shareURL('facebook')}`}
            target="_blank"
            onClick={() => onShare('facebook')}
          >
            <Icon type="facebook" color="#fff" size={30} />
          </a>
          <a
            className={classnames(styles.twitter, {
              [styles.disable]: !isLoadedShareImg,
            })}
            href={`https://twitter.com/share?url=${shareURL('twitter')}`}
            target="_blank"
            onClick={() => onShare('twitter')}
          >
            <Icon type="twitter" color="#fff" size={30} />
          </a>
          <a
            className={classnames(styles.whatsApp, {
              [styles.disable]: !isLoadedShareImg,
            })}
            href={`whatsapp://send?text=${shareURL('whatsapp')}`}
            target="_blank"
            onClick={() => onShare('whatsapp')}
          >
            <Icon type="whatsApp" color="#fff" size={30} />
          </a>
          <a
            className={classnames(styles.messenger, {
              [styles.disable]: !isLoadedShareImg,
            })}
            href={`fb-messenger://share/?link=${shareURL('messenger')}`}
            target="_blank"
            onClick={() => onShare('messenger')}
          >
            <Icon type="messenger" color="#fff" size={30} />
          </a>
        </div>
        <div className={styles.footer} onClick={handleClose}>
          <span>{i18n.format('modules.global.cancel')}</span>
        </div>
      </Drawer>
    </div>
  )
}

export default ShareLinks;
